import { PlusOutlined } from "@ant-design/icons"
import { Button, Checkbox, Form } from "antd"
import dayjs from "dayjs"
import { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useSearchParams } from "react-router-dom"
import { modelSelector } from "store/selectors"
import { updatedSeasonal } from "store/trips"
import { Card } from "ui/Card"
import { DateRangeBlock } from "../components/scheduling/DateRangeBlock/DateRangeBlock"
import { SeasonTypeSelector } from "../components/scheduling/SeasonTypeSelector/SeasonTypeSelector"
import { DurationAndCutOff } from "../components/scheduling/DurationAndCutOff/DurationAndCutOff"
import { AvailabilityDisplay } from "../components/scheduling/AvailabilityDisplay/AvailabilityDisplay"
import { TicketedSchedule } from "../components/scheduling/TicketedSchedule/TicketedSchedule"
import { ExperienceDurationType } from "../components/scheduling/ExperienceDurationType/ExperienceDurationType"
import { PrivateCutOff } from "../components/scheduling/PrivateCutOff/PrivateCutOff"
import { AvailabilitySchedule } from "../components/scheduling/AvailabilitySchedule/AvailabilitySchedule"
import { MultiDaySchedule } from "../components/scheduling/MultiDaySchedule/MultiDaySchedule"
import { SEASON_TYPES } from "pagesV2/pro/Experiences/ListingScheduling/utils/constants"
import { buildSchedulingPayload } from "../mappers/toApiPayload"
import { useWizardContext } from "../WizardContext"

/**
 * Season date fields are named by position (seasonStart_N / seasonEnd_N) and
 * `timeBlocks` is kept dense ([0..n-1]) so those positions always match the
 * compacted `available_date_ranges` array the backend returns on save.
 *
 * Because positions are reused, a freed slot must be stored as `null`, not
 * `undefined`: AntD re-applies a Form.Item's `initialValue` whenever the store
 * value is `undefined`, which would resurrect the removed block's dates inside
 * the next block added at that position. Every consumer of these fields guards
 * with `start && end`, so `null` behaves like `undefined` everywhere else.
 */
const clearedRangeAt = (position) => ({
  [`seasonStart_${position}`]: null,
  [`seasonEnd_${position}`]: null
})

export const SchedulingStep = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [searchParams] = useSearchParams()
  const { form, stepSaveHandlersRef, stepValidateHandlersRef } = useWizardContext()
  const listingType = searchParams.get("type")
  const isTicketed = listingType === "ticketed"
  const trip = useSelector(modelSelector("trip"))
  // Keyed by the trip actually initialized from, not a boolean: `trip` gets a new
  // identity on every request/response (the reducer mutates it, so the loading
  // flag alone changes it), and re-running the sync mid-save would re-seed the
  // blocks from the pre-save array and stomp the user's pending edits.
  const initializedTripIdRef = useRef(null)

  const durationType = Form.useWatch("duration_type", form)
  const isMultiDay = durationType === "multi_day"

  // React state is the source of truth; form is kept in sync for payload building and persistence
  const [isSeasonalExperience, setIsSeasonalExperience] = useState(
    () => form.getFieldValue("isSeasonalExperience") ?? false
  )
  const [timeBlocks, setTimeBlocks] = useState(
    () => form.getFieldValue("timeBlocks") || []
  )

  // Re-sync when WizardProvider loads existing trip data after this component mounts
  useEffect(() => {
    if (!trip?.id || initializedTripIdRef.current === trip.id) return

    const isSeasonal = trip.seasonal === true
    setIsSeasonalExperience(isSeasonal)
    form.setFieldValue("isSeasonalExperience", isSeasonal)

    if (isSeasonal && trip?.available_date_ranges?.length > 0) {
      const previousBlockCount = (form.getFieldValue("timeBlocks") || []).length
      const rangeValues = {}
      trip.available_date_ranges.forEach((range, index) => {
        rangeValues[`seasonStart_${index}`] = range.start ? dayjs(range.start) : null
        rangeValues[`seasonEnd_${index}`] = range.end ? dayjs(range.end) : null
      })
      const blocks = trip.available_date_ranges.map((_, i) => i)
      // The step unmounts on tab change, so this re-sync can follow a block list
      // that was longer than the server's. Clear the freed tail, otherwise the
      // next added block reuses a stale position's dates.
      for (let position = blocks.length; position < previousBlockCount; position += 1) {
        Object.assign(rangeValues, clearedRangeAt(position))
      }
      setTimeBlocks(blocks)
      form.setFieldsValue({ ...rangeValues, timeBlocks: blocks })
    } else if (isSeasonal) {
      setTimeBlocks([0])
      form.setFieldValue("timeBlocks", [0])
    }

    initializedTripIdRef.current = trip.id
  }, [form, trip])

  const handleSeasonChange = (e) => {
    const isSeasonal = e.target.value === SEASON_TYPES.SEASONAL
    setIsSeasonalExperience(isSeasonal)
    form.setFieldValue("isSeasonalExperience", isSeasonal)
    if (isSeasonal && timeBlocks.length === 0) {
      setTimeBlocks([0])
      form.setFieldValue("timeBlocks", [0])
    }
    form.setFieldsValue({ seasonal: isSeasonal })
    dispatch(updatedSeasonal(isSeasonal))
  }

  // Both handlers derive the next list from `timeBlocks` rather than from a
  // setState updater: they touch the form store, and StrictMode double-invokes
  // updaters, which would re-read values the first pass had already moved.
  const handleAddTimeBlock = () => {
    const newPosition = timeBlocks.length
    const next = [...timeBlocks, newPosition]
    setTimeBlocks(next)
    form.setFieldsValue({ timeBlocks: next, ...clearedRangeAt(newPosition) })
  }

  const handleRemoveTimeBlock = (removedPosition) => {
    const survivors = timeBlocks.filter((blockIndex) => blockIndex !== removedPosition)
    const next = survivors.map((_, position) => position)

    // Re-pack the surviving dates down into the dense positions, then clear the
    // freed tail. Removing without re-packing leaves form positions out of step
    // with the array the backend compacts on save.
    const repackedRanges = {}
    survivors.forEach((survivingPosition, position) => {
      repackedRanges[`seasonStart_${position}`] = form.getFieldValue(`seasonStart_${survivingPosition}`) ?? null
      repackedRanges[`seasonEnd_${position}`] = form.getFieldValue(`seasonEnd_${survivingPosition}`) ?? null
    })
    for (let position = next.length; position < timeBlocks.length; position += 1) {
      Object.assign(repackedRanges, clearedRangeAt(position))
    }

    setTimeBlocks(next)
    form.setFieldsValue({ timeBlocks: next, ...repackedRanges })
  }

  // Read live form values on demand instead of memoizing them: `disabledDate`
  // runs when a picker opens, and a memo over imperative form reads went stale
  // the moment a date changed, leaving overlapping ranges selectable.
  const getAllSelectedDateRanges = useCallback(
    () =>
      (form.getFieldValue("timeBlocks") || [])
        .map((blockIndex) => {
          const start = form.getFieldValue(`seasonStart_${blockIndex}`)
          const end = form.getFieldValue(`seasonEnd_${blockIndex}`)
          return start && end ? { blockIndex, start, end } : null
        })
        .filter(Boolean),
    [form]
  )

  useEffect(() => {
    stepSaveHandlersRef.current["scheduling"] = async () => {
      const values = form.getFieldsValue()
      // These fields are set via setFieldValue (no Form.Item), so getFieldsValue() omits them — read directly
      const timeSlots = form.getFieldValue("time_slots") || []
      const availability_schedules = form.getFieldValue("availability_schedules") || []
      const multi_day_start_days = form.getFieldValue("multi_day_start_days") || []
      const multi_day_start_time = form.getFieldValue("multi_day_start_time")
      const multi_day_schedule_id = form.getFieldValue("multi_day_schedule_id")
      // A shared tour's duration_type is set on the Ticket Type step, so it isn't on this step's fields.
      const duration_type = values.duration_type ?? form.getFieldValue("duration_type")
      // Season date fields are dynamic names (seasonStart_N / seasonEnd_N) — read
      // each one directly so they aren't silently dropped from getFieldsValue().
      const seasonDateValues = {}
      timeBlocks.forEach((blockIndex) => {
        seasonDateValues[`seasonStart_${blockIndex}`] = form.getFieldValue(`seasonStart_${blockIndex}`)
        seasonDateValues[`seasonEnd_${blockIndex}`] = form.getFieldValue(`seasonEnd_${blockIndex}`)
      })
      return buildSchedulingPayload(
        { ...values, ...seasonDateValues, duration_type, time_slots: timeSlots, availability_schedules, multi_day_start_days, multi_day_start_time, multi_day_schedule_id },
        { isSeasonalExperience, timeBlocks, isTicketed }
      )
    }
    return () => {
      delete stepSaveHandlersRef.current["scheduling"]
    }
  }, [form, stepSaveHandlersRef, isSeasonalExperience, timeBlocks, isTicketed])

  useEffect(() => {
    stepValidateHandlersRef.current["scheduling"] = async () => {
      if (isTicketed) {
        const timeSlots = form.getFieldValue("time_slots") || []
        if (timeSlots.length === 0) {
          const err = new Error("At least one scheduled slot is required")
          err.errorFields = [{ errors: ["At least one scheduled slot is required"] }]
          throw err
        }
        return
      }

      // Seasonality is offered for every non-ticketed experience, single- or
      // multi-day, so this runs before the per-duration-type branches — both of
      // those return early, and multi-day used to skip this check entirely.
      //
      // The `required` rules on the DateRangeBlock pickers cannot stand in for
      // this: seasonStart_N / seasonEnd_N are dynamically named, so they are
      // absent from stepConfig's scheduling `fields` list and the
      // form.validateFields(fields) pass in useExperienceWizard never sees them.
      if (isSeasonalExperience && timeBlocks.length > 0) {
        const missing = timeBlocks.some((blockIndex) => {
          const start = form.getFieldValue(`seasonStart_${blockIndex}`)
          const end = form.getFieldValue(`seasonEnd_${blockIndex}`)
          return !start || !end
        })
        if (missing) {
          const err = new Error(t("listings.tabs.scheduling.sections.seasons.dates_required", { defaultValue: "From and To dates are required for seasonal experiences" }))
          err.errorFields = [{ errors: [err.message] }]
          throw err
        }
      }

      // Multi-day: require at least one start day and a start time.
      if (isMultiDay) {
        const durationMode = form.getFieldValue("duration_mode")
        if (durationMode === "dynamic") {
          const minDays = form.getFieldValue("min_duration_days")
          const maxDays = form.getFieldValue("max_duration_days")
          if (!minDays || minDays < 2) {
            const err = new Error(t("listings.tabs.scheduling.sections.duration_type.min_duration_required", { defaultValue: "Min duration is required" }))
            err.errorFields = [{ errors: [err.message] }]
            throw err
          }
          if (!maxDays || maxDays < 2 || maxDays < minDays) {
            const err = new Error(t("listings.tabs.scheduling.sections.duration_type.max_greater_than_min", { defaultValue: "Max duration cannot be lower than min duration" }))
            err.errorFields = [{ errors: [err.message] }]
            throw err
          }
        }
        const startDays = form.getFieldValue("multi_day_start_days") || []
        const startTime = form.getFieldValue("multi_day_start_time")
        if (startDays.length === 0) {
          const err = new Error(t("listings.tabs.scheduling.sections.multi_day_schedule.start_days_required", { defaultValue: "Select at least one start day" }))
          err.errorFields = [{ errors: [err.message] }]
          throw err
        }
        if (!startTime) {
          const err = new Error(t("listings.tabs.scheduling.sections.multi_day_schedule.start_time_required", { defaultValue: "Start time is required" }))
          err.errorFields = [{ errors: [err.message] }]
          throw err
        }
        return
      }

      // Private single-day: require at least one availability schedule.
      const allSchedules = form.getFieldValue("availability_schedules") || []
      const hasSchedule = allSchedules.some(
        (schedule) =>
          !schedule._destroy &&
          schedule.schedule_type !== "multi_day" &&
          !(schedule.schedule_type == null && schedule.to_time == null && schedule.interval == null)
      )
      if (!hasSchedule) {
        const err = new Error(t("experiences.scheduling.errors.schedule_required"))
        err.errorFields = [{ errors: [err.message] }]
        throw err
      }
    }
    return () => {
      delete stepValidateHandlersRef.current["scheduling"]
    }
  }, [form, stepValidateHandlersRef, isTicketed, isMultiDay, isSeasonalExperience, timeBlocks, t])

  return (
    <div>
      {isTicketed && <DurationAndCutOff />}
      {isTicketed && <TicketedSchedule />}
      {isTicketed && <AvailabilityDisplay />}

      {!isTicketed && (
        <>
          {/* Seasonality */}
          <Card className="mb-3xl" title={t("listings.tabs.scheduling.sections.seasons.title")}>
            <SeasonTypeSelector onChange={handleSeasonChange} value={isSeasonalExperience ? SEASON_TYPES.SEASONAL : SEASON_TYPES.ALL_YEAR} />
            {isSeasonalExperience && (
              <>
                {timeBlocks.map((blockIndex) => {
                  const dateRange = trip?.available_date_ranges?.[blockIndex]
                  return (
                    <DateRangeBlock
                      key={blockIndex}
                      blockIndex={blockIndex}
                      onDelete={handleRemoveTimeBlock}
                      isSeasonalActive={isSeasonalExperience}
                      initialStartDate={dateRange?.start ? dayjs(dateRange.start) : undefined}
                      initialEndDate={dateRange?.end ? dayjs(dateRange.end) : undefined}
                      getAllRanges={getAllSelectedDateRanges}
                      timeBlocks={timeBlocks}
                    />
                  )
                })}
                <Button type="link" icon={<PlusOutlined />} onClick={handleAddTimeBlock} className="mt-m pl-0">
                  {t("listings.tabs.scheduling.sections.seasons.add_time_block")}
                </Button>
                <Form.Item name="recurringYearly" valuePropName="checked" className="mt-l">
                  <Checkbox>{t("listings.tabs.scheduling.sections.seasons.recurring_yearly")}</Checkbox>
                </Form.Item>
              </>
            )}
          </Card>

          <ExperienceDurationType form={form} />

          {/* Cut-off */}
          <PrivateCutOff />

          {/* Availability schedule: multi-day uses a simplified day+time picker;
              single-day uses the full per-slot schedule builder. */}
          {isMultiDay
            ? <MultiDaySchedule form={form} />
            : <AvailabilitySchedule form={form} />
          }
        </>
      )}
    </div>
  )
}
